import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { runAction } from '@/utils/errors';
import { getOpeningStores, getStoreByNumber } from '@/api/stores';
import { $storeList, setStoreList } from '@/models/store';
import { $user } from '@/models/user';
import { GroupsEnum } from '@/types/users';
import type { Store, StoreWorkItem } from '@/types/store';
import { hasAnyGroup } from '@/utils/groups';

const parseWorklist = (worklist: unknown): StoreWorkItem[] => {
  if (Array.isArray(worklist)) {
    return worklist.flatMap((item) => parseWorklist(item));
  }

  if (typeof worklist === 'object' && worklist !== null) {
    return [worklist as StoreWorkItem];
  }

  if (typeof worklist !== 'string' || !worklist.trim()) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(worklist.replace(/'/g, '"'));
    return parseWorklist(parsed);
  } catch {
    return [];
  }
};

const removeDuplicateStores = (stores: Store[]) =>
  stores.filter(
    (store, index, list) => index === list.findIndex((item) => item.number === store.number),
  );

export const normalizeStoreId = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const storeId = String(value).trim();
  return storeId || null;
};

export const useStores = () => {
  const user = useUnit($user);
  const stores = useUnit($storeList);
  const [loading, setLoading] = useState(stores.length === 0);
  const [error, setError] = useState(false);
  const worklist = user?.worklist;
  const isOpeningManager = hasAnyGroup(user?.groups, [GroupsEnum.OPENING_MANAGER]);

  useEffect(() => {
    if (!user || stores.length > 0) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    setLoading(true);
    setError(false);
    runAction(
      async () => {
        const worklistStoresPromise = Promise.all(
          parseWorklist(worklist)
            .map((item) => normalizeStoreId(item.storeId))
            .filter((storeId): storeId is string => storeId !== null)
            .map((storeId) => getStoreByNumber(storeId, controller.signal)),
        );
        const [worklistStores, openingStores] = await Promise.all([
          worklistStoresPromise,
          isOpeningManager ? getOpeningStores(controller.signal) : Promise.resolve([]),
        ]);
        const loadedStores = [...worklistStores, ...openingStores];

        if (!controller.signal.aborted) {
          setStoreList(removeDuplicateStores(loadedStores.filter(Boolean)));
        }
      },
      { signal: controller.signal, onError: () => setError(true) },
    ).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });

    return () => controller.abort();
  }, [isOpeningManager, stores.length, user, worklist]);

  return { stores, loading, error };
};
